/**
 * @fileoverview NotesCard component for displaying and managing notes with filtering and search functionality
 * @module components/cards/NotesCard
 * @requires react
 * @requires @mui/material
 * @requires @mui/icons-material
 * @requires ../../utils/capitalize
 * @requires ./InfoCard
 */

import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

import {
  Box,
  Button,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Popover,
  Tooltip,
  IconButton,
} from "@mui/material";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import PushPinIcon from "@mui/icons-material/PushPin";

import OutreachIcon from "../../assets/icons/OutreachIcon.svg";
import CommonSearchBar from "../common/searchBar/CommonSearchBar";
import InfoCard from "./InfoCard";
import Profile from "../../assets/icons/profile.svg";
import FocusableScrollContainer from "../common/FocusableScrollContainer";
import { placeholderText } from "../../constants/colors";

import formatDateTimeUtil from "../../utils/formatDateTime";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
/**
 * NotesCard Component
 * A component for displaying and managing notes with filtering and search capabilities.
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.notes - Array of note objects to display
 * @param {string} [props.title="Notes"] - Title of the notes card
 * @param {Function} [props.onNoteTypeChange] - Callback when note type filter changes
 * @param {Array<string>} [props.selectedNoteType=[]] - Currently selected note types
 * @param {string} [props.searchQuery=""] - Current search query
 * @param {Function} [props.onSearch] - Callback for search functionality
 * @param {Function} [props.onTogglePin] - Callback when a note's pin status is toggled
 * @param {Function} [props.onEdit] - Callback when a note is edited
 * @param {boolean} [props.inQuickview=false] - Whether the card is in quickview mode
 * @param {Array<Object>} [props.programStatusMap=[]] - Mapping of program statuses
 * @returns {JSX.Element} The NotesCard component
 */
const NotesCard = ({
  notes: propNotes,
  title = "Notes",
  onNoteTypeChange,
  selectedNoteType = [],
  searchQuery: propSearchQuery = "",
  onSearch,
  onTogglePin,
  onEdit,

  inQuickview = false,
  setIsNoteModalOpen,
  setIsNotesViewModalOpen,
  canWrite = true,
  showAddButton = true,
}) => {
  const showCloseButton = typeof setIsNotesViewModalOpen === "function";
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState(propSearchQuery);
  const [selectedTypes, setSelectedTypes] = useState(
    Array.isArray(selectedNoteType) ? selectedNoteType : [],
  );

  const filterButtonRef = useRef(null);

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    onSearch(newValue);
  };

  const noteTypes = ["All", "Archived"];

  useEffect(() => {
    if (selectedNoteType !== undefined) {
      setSelectedTypes(
        Array.isArray(selectedNoteType) ? selectedNoteType : [selectedNoteType],
      );
    }

    setSearchTerm(propSearchQuery);
  }, [selectedNoteType, propSearchQuery]);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleTypeToggle = (type) => {
    const newSelected = [type];

    setSelectedTypes(newSelected);
    onNoteTypeChange(newSelected);
  };

  const open = Boolean(anchorEl);
  const id = open ? "note-type-filter" : undefined;

  const [notes, setNotes] = useState(propNotes);

  React.useEffect(() => {
    if (propNotes) {
      setNotes(propNotes);
    }
  }, [propNotes]);

  const defaultSelectedTypes = noteTypes.filter(
    (type) => type.toLowerCase() !== "archived",
  );

  const effectiveSelectedTypes =
    selectedTypes.length === 0 ? [...defaultSelectedTypes] : selectedTypes;

  useEffect(() => {
    if (selectedTypes.length === 0 && onNoteTypeChange) {
      onNoteTypeChange([...defaultSelectedTypes]);
    }
    /* eslint-disable no-unused-vars */
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  const filteredNotes = (notes || [])
    .filter((note) => {
      const isArchived = note.status === "ARCHIVED";
      const noteType = note.type || "General";
      const searchLower = searchTerm.toLowerCase();

      if (searchTerm) {
        const searchFields = [note.content || ""];

        const matchesSearch = searchFields.some((field) =>
          field.toLowerCase().includes(searchLower),
        );

        if (!matchesSearch) return false;
      }

      if (effectiveSelectedTypes.includes("All") && !isArchived) {
        return true;
      }

      if (effectiveSelectedTypes.includes("Archived") && isArchived) {
        return true;
      }

      return effectiveSelectedTypes.some(
        (selectedType) => selectedType.toLowerCase() === noteType.toLowerCase(),
      );
    })
    .sort((a, b) => {
      if (a.pinNote && !b.pinNote) return -1;
      if (!a.pinNote && b.pinNote) return 1;

      const dateA = a.updateAt || a.createdAt || a.date || 0;
      const dateB = b.updateAt || b.createdAt || b.date || 0;
      return new Date(dateB) - new Date(dateA);
    });

  const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <InfoCard
      showDivider={false}
      title={
        <Box
          component="span"
          sx={{
            backgroundColor: "#403C6F",
            color: "#fff",
            padding: "16px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
            fontWeight: "600 !important",
            fontFamily:
              "Inter, ui-sans-serif, system-ui, sans-serif !important",
            lineHeight: "1.35 !important",
            margin: "0 !important",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          {title}
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              position: "absolute",
              right: "30px",
            }}
          >
            {showAddButton && (
              <IconButton
                onClick={() => {
                  if (!canWrite || typeof setIsNoteModalOpen !== "function")
                    return;
                  setIsNoteModalOpen(true);
                }}
                disabled={!canWrite}
                aria-disabled={!canWrite}
                sx={{
                  color: canWrite ? "#fff" : "#999893",
                  padding: 0,
                  "&:hover": { opacity: canWrite ? 0.8 : 1 },
                  "&.Mui-disabled": {
                    cursor: "not-allowed",
                    color: "#999893",
                    pointerEvents: "auto !important",
                  },
                }}
              >
                <AddIcon sx={{ fontSize: "24px" }} />
              </IconButton>
            )}
            {showCloseButton && (
              <IconButton
                onClick={() => setIsNotesViewModalOpen(false)}
                sx={{
                  color: "#fff",
                  padding: 0,
                  "&:hover": { opacity: 0.8 },
                }}
              >
                <CloseIcon sx={{ fontSize: "24px" }} />
              </IconButton>
            )}
          </Box>
        </Box>
      }
      contentPadding={0}
      showEditButton={false}
      sx={{
        borderRadius: 2,
        border: "1px solid #E8E8E9",
        boxShadow: "none",
        boxSizing: "border-box",
        "& .MuiBox-root": { mb: 0 },
        "& .MuiDivider-root": { my: 0.5 },
        ...(inQuickview && {
          display: "flex",
          flexDirection: "column",
          height: "auto",
          minHeight: "100%",
          maxHeight: "80vh",
          overflow: "hidden",
        }),
      }}
      quickview={inQuickview}
    >
      <Box
        sx={{
          ...(inQuickview && {
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 1,

            pb: 1,
          }),
        }}
      >
        <Box mb={1} sx={{ position: "relative" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            mb={1}
            pr={2}
            gap={1}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CommonSearchBar
                placeholder="Search notes..."
                value={searchTerm}
                width="250px"
                onChange={(e) => handleSearchChange(e)}
                onSearch={(value) => {
                  setSearchTerm(value);
                  onSearch(value);
                }}
              />
            </Box>
            <Box flexShrink={0}>
              <Button
                variant="outlined"
                size="small"
                aria-haspopup="true"
                aria-controls={id}
                aria-expanded={open ? "true" : undefined}
                aria-label={`${selectedTypes.length} filters applied`}
                onClick={handleFilterClick}
                endIcon={<FilterListRoundedIcon />}
                sx={{
                  textTransform: "none",
                  color: "#403C6F",
                  borderColor: "#403C6F",
                  backgroundColor: "#ECECF1",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    borderColor: "#403C6F",
                    backgroundColor: "#ECECF1",
                    color: "#403C6F",
                  },
                  "& .MuiButton-endIcon": {
                    color: "#403C6F",
                    marginLeft: "2px",
                  },
                  borderRadius: 20,
                }}
                ref={filterButtonRef}
              >
                {selectedTypes.includes("Archived") ? (
                  <span>Archived</span>
                ) : (
                  "All Notes"
                )}
              </Button>
            </Box>
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleFilterClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              disableScrollLock
              sx={{
                ...(!inQuickview && {
                  position: "fixed !important",
                  transform: "none !important",
                  "& .MuiPopover-paper": {
                    position: "static !important",
                    transform: "none !important",
                  },
                }),
                "& .MuiPopover-paper": {
                  backgroundColor: "#6E6D92",
                  color: "#fff",
                  borderRadius: "10px",
                  boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                  minWidth: 265,
                  width: 265,
                  maxHeight: "70vh",
                  overflowY: "auto",
                  "&::-webkit-scrollbar": { width: "6px" },
                  "&::-webkit-scrollbar-track": {
                    background: "rgba(255,255,255,0.1)",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(255,255,255,0.3)",
                    borderRadius: "10px",
                    "&:hover": { background: "rgba(255,255,255,0.4)" },
                  },
                },
                "& .MuiMenuItem-root": {
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                },
              }}
            >
              <Box
                role="menu"
                aria-label="Note type filters"
                sx={{
                  p: 2,
                  gap: 2,
                  maxHeight: "calc(70vh - 32px)",
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                {noteTypes.map((type, idx) => {
                  const isSelected = selectedTypes.includes(type);
                  return (
                    <MenuItem
                      key={type}
                      role="menuitemcheckbox"
                      aria-checked={isSelected}
                      onClick={() => handleTypeToggle(type)}
                      sx={{
                        width: 239,
                        height: 40,
                        justifyContent: "space-between",
                        opacity: 1,
                        p: 0,
                        pt: "8px",
                        pr: "16px",
                        pb: "8px",
                        pl: "16px",
                        borderRadius: "4px",
                        color: "#fff",
                        backgroundColor: isSelected ? "#403C6F" : "transparent",
                        alignItems: "center",
                        mb: idx === noteTypes.length - 1 ? 0 : "4px",
                        "&:hover": {
                          backgroundColor: isSelected
                            ? "#403C6F"
                            : "rgba(255,255,255,0.08)",
                        },
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        width="100%"
                        justifyContent="space-between"
                      >
                        <Box sx={{ flex: 1, textAlign: "left" }}>
                          <ListItemText
                            primary={type}
                            slotProps={{
                              primaryTypography: {
                                sx: {
                                  color: "#fff",
                                  textAlign: "left",
                                  margin: 0,
                                },
                              },
                            }}
                          />
                        </Box>
                        <ListItemIcon sx={{ minWidth: 32, marginLeft: "8px" }}>
                          <span
                            aria-hidden="true"
                            style={{
                              width: 16,
                              height: 16,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: 4,
                              border: "2px solid #fff",
                              backgroundColor: isSelected
                                ? "#fff"
                                : "transparent",
                            }}
                          >
                            {isSelected && (
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#403C6F"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ display: "block" }}
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </span>
                        </ListItemIcon>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Box>
            </Popover>
          </Box>
          <Box mb={2} />
        </Box>
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            pr: 1,
            mt: 1,
            ...(inQuickview && {
              display: "flex",
              flexDirection: "column",
              height: "auto",
              maxHeight: "100%",
              minHeight: 0,
            }),
          }}
        >
          <FocusableScrollContainer
            component="section"
            label="Notes list"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              pr: "8px",
              pb: "8px",
              height: "55vh",
              maxHeight: "100vh",
              overflowY: "auto",
              scrollbarWidth: "thin",
            }}
          >
            {filteredNotes.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  color: "#0E0D09",
                  fontSize: "16px",
                  fontWeight: 700,
                  flexGrow: 1,
                  minHeight: "250px",
                  height: "100%",
                }}
              >
                {searchTerm ? "No Notes found" : "No Notes available"}
              </Box>
            ) : (
              filteredNotes.map((note, i) => {
                const fullContent =
                  typeof note.content === "string"
                    ? note.content
                    : JSON.stringify(note.content);
                const isLong = fullContent.length > 256;
                const noteContent = isLong
                  ? fullContent.slice(0, 256)
                  : fullContent;

                const formatDateTime = (dateString) =>
                  formatDateTimeUtil(dateString, { timeZone: systemTimeZone });
                const creationDate = note.date || note.createdAt;
                const displayDateTime = formatDateTime(creationDate);
                const authorName = note.author || "Unknown";
                return (
                  <Box
                    key={note.id || i}
                    component={isLong ? "div" : "button"}
                    onClick={() => {
                      if (!isLong && onEdit) onEdit(note);
                    }}
                    sx={{
                      width: "100%",
                      textAlign: "left",
                      border: "1px solid #E8E8E9",
                      borderRadius: "8px",
                      mb: 2,
                      cursor: isLong ? "default" : "pointer",
                      transition: "all 0.2s",
                      position: note.pinNote ? "sticky" : "relative",
                      top: note.pinNote ? 0 : "auto",
                      zIndex: note.pinNote ? 10 : 1,
                      backgroundColor: "#fff",
                      boxShadow: note.pinNote
                        ? "0 2px 4px rgba(0,0,0,0.1)"
                        : "none",
                      "&:last-child": { mb: 0 },
                      padding: 2,
                      background: "#fff",
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      gap={1}
                      mb={1.5}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          fontWeight: 500,
                          fontSize: "14px",
                          lineHeight: "20px",
                          color: "#403C6F",
                          whiteSpace: "pre-line",
                          wordBreak: "break-word",
                        }}
                      >
                        {noteContent}
                        {isLong && (
                          <Box
                            component="span"
                            sx={{
                              color: "#403C6F",
                              fontWeight: 700,
                              ml: 0.5,
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onEdit) onEdit(note);
                            }}
                          >
                            ...Read more
                          </Box>
                        )}
                      </Box>
                      {note.pinNote && (
                        <Box
                          onClick={async (e) => {
                            e.stopPropagation();
                            const updatedNotes = notes.map((n) =>
                              n.id === note.id
                                ? { ...n, pinNote: !n.pinNote }
                                : n,
                            );
                            setNotes(updatedNotes);
                            if (onTogglePin && note.id) {
                              try {
                                await onTogglePin(note.id);
                              } catch (error) {
                                console.error("Unexpected error", error);
                                const revertedNotes = notes.map((n) =>
                                  n.id === note.id
                                    ? { ...n, pinNote: !n.pinNote }
                                    : n,
                                );
                                setNotes(revertedNotes);
                              }
                            }
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            flexShrink: 0,
                            mt: "3px",
                            "&:hover": {
                              "& .MuiSvgIcon-root": { color: "#2A2866" },
                            },
                          }}
                        >
                          <PushPinIcon
                            fontSize="small"
                            sx={{
                              color: note.pinNote ? "#403C6F" : "#E0E0E0",
                              transition: "color 0.2s",
                              "&:hover": { color: "#2A2866" },
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 1,
                        position: "relative",
                      }}
                    >
                      {note.type?.toLowerCase() === "outreach" && (
                        <Box sx={{ position: "absolute", right: 0, bottom: 0 }}>
                          <Tooltip
                            title={
                              <Box
                                sx={{
                                  fontWeight: 400,
                                  fontSize: "12px",
                                  lineHeight: "16px",
                                }}
                              >
                                This outreach note is added for{" "}
                                {note.programName || "the program"}
                              </Box>
                            }
                            arrow
                            placement="bottom"
                            slotProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: "#403C6F",
                                  "& .MuiTooltip-arrow": {
                                    color: "#403C6F",
                                  },
                                  boxShadow: "none",
                                  "& .MuiTooltip-tooltip": {
                                    fontWeight: 400,
                                  },
                                },
                              },
                            }}
                          >
                            <Box
                              component="img"
                              src={OutreachIcon}
                              alt="Outreach"
                              sx={{
                                width: 20,
                                height: 20,
                                position: "absolute",
                                right: 0,
                                bottom: 0,
                              }}
                            />
                          </Tooltip>
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <Box
                          component="img"
                          src={Profile}
                          alt="Author"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            e.currentTarget.nextSibling.style.display =
                              "inline-flex";
                          }}
                          sx={{
                            width: "16px",
                            height: "16px",
                            filter:
                              "invert(55%) sepia(10%) saturate(1000%) hue-rotate(210deg) brightness(90%) contrast(85%)", // This approximates #7F7C9F
                          }}
                        />
                        <Box
                          sx={{
                            display: "none",
                            width: 16,
                            height: 16,
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "50%",
                            backgroundColor: "#ECECF1",
                            fontSize: 10,
                            fontWeight: 600,
                            color: "#403C6F",
                          }}
                        >
                          {(authorName || "U").charAt(0).toUpperCase()}
                        </Box>
                        <Box
                          sx={{
                            fontWeight: 600,
                            fontSize: "12px",
                            color: placeholderText,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {authorName}
                          {displayDateTime && (
                            <>
                              <span style={{ margin: "0 1px" }}>|</span>
                              <span style={{ fontSize: "12px" }}>
                                Created on: {displayDateTime}
                              </span>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </FocusableScrollContainer>
        </Box>
      </Box>
    </InfoCard>
  );
};

NotesCard.propTypes = {
  notes: PropTypes.array,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onNoteTypeChange: PropTypes.func,
  selectedNoteType: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  searchQuery: PropTypes.string,
  onSearch: PropTypes.func,
  onTogglePin: PropTypes.func,
  onEdit: PropTypes.func,
  inQuickview: PropTypes.bool,
  setIsNoteModalOpen: PropTypes.func,
  setIsNotesViewModalOpen: PropTypes.func,
  canWrite: PropTypes.bool,
  showAddButton: PropTypes.bool,
};

export default NotesCard;
